/**
 * The totals row of an ArTable: one cell per visible column, holding the
 * column's aggregation read from the server-cached aggregation document, or
 * recomputed over the printed rows in PDF export. Consumer: `ArTableBody`.
 *
 * In card mode (below `md`) it becomes a summary card: a "Total" title, then a
 * label stacked above the value for each column that carries an aggregation only — the
 * columns without one, and the checkbox and actions spacers, are not rendered.
 */
import { CSSProperties, FC, JSX, memo, useContext } from 'react';
import { theme, Typography } from 'antd';
import { usePdfExportMode } from '../usePdfExportMode';
import { proxy, useSnapshot } from 'valtio';
import { useStoreCollection, useStoreDocument } from '@/store';
import type { DocEntry } from '@/store';
import { computeArRowFormulas, computeColumnAggregation, createArFormulaEngineConfig } from '#types';
import type { ArAggregationRow } from '#types';
import type {
  ArCellValue,
  ArColumnDefinition,
  ArColumnField,
  ArRowData,
  ArRowId,
  ArTableType,
} from '#types/System.Artifacts.types';
import { UIElementType } from '#types/System.types';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import resolveArColumnLabel from '../column/resolveArColumnLabel';
import ArCellRouter from './ArCell/ArCellRouter';
import ArAggregationTag from './ArAggregationTag';
import { formatFieldValueParts } from '../../CompositeField/formatPreviewValue';
import { formattedCellValueToString } from '../../CompositeField/formattedCellValue';

const { Text } = Typography;

const EMPTY_AGG_ENTRY = proxy<DocEntry<ArRowData>>({ data: {} as ArRowData });

type ArTableAggregationsProps = {
  stickyFirstColumn?: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  columns: ArColumnDefinition[];
  /**
   * Suppresses the whole totals row. Set while a split narrows the interactive
   * table, where the only total available is the server-cached whole-table one
   * and printing it beside a subset of rows would misreport it. PDF export
   * recomputes over `pdfRowIds`, so it stays visible there (SYD-2679).
   */
  hidden?: boolean;
  /**
   * When set, totals are recomputed client-side over these rows instead of
   * read from the server-cached aggregation document. Used for PDF exports
   * so totals match the printed row subset.
   */
  pdfRowIds?: readonly ArRowId[];
  cardMode?: boolean;
};

const ArTableAggregations: FC<ArTableAggregationsProps> = ({
  stickyFirstColumn = true,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  columns,
  hidden = false,
  pdfRowIds,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const pdfExportMode = usePdfExportMode();

  const schemaStore = useStoreDocument<ArTableType>('schema');
  const schemaSnap = schemaStore.useSnapshot(schemaStore.state);

  const rowsStore = useStoreCollection<ArRowData>('rows');
  const rowsState = rowsStore.state;
  const rowsSnap = rowsStore.useSnapshot(rowsState);
  const hasAggregationEntry = rowsSnap.docs.has(AR_TABLE_AGGREGATIONS_ROW_ID);
  const aggEntry = hasAggregationEntry
    ? rowsState.docs.get(AR_TABLE_AGGREGATIONS_ROW_ID) ?? EMPTY_AGG_ENTRY
    : EMPTY_AGG_ENTRY;
  const aggSnap = useSnapshot(aggEntry);

  const aggregationsEnabled = schemaSnap.data?.aggregationsEnabled === true;
  if (!aggregationsEnabled) return null;
  if (hidden) return null;

  const schemaColumns = schemaSnap.data?.columns ?? [];
  const hasAggregations = schemaColumns.some((col) =>
    col.uiElementType === UIElementType.Field && !!(col as ArColumnField).aggregation
  );

  if (!hasAggregations) return null;

  /**
   * "Computing" covers both an explicitly stale cache and a cache that is not on
   * screen yet: the aggregation document carries no `order`, so it sits outside
   * the paginated rows query and its slot is dropped whenever the rows store is
   * rebuilt, until the dedicated listener in `useArTableAggregations` re-delivers
   * it (SYD-2311). Both states must read as in-progress, never as an empty total.
   * An empty table computes nothing, so it shows no loader.
   *
   * In PDF mode, totals are always recomputed client-side from the printed
   * row subset (`pdfRowIds`), so the cached flag and its loader are irrelevant.
   */
  const isComputing = !pdfExportMode
    && rowsSnap.docIds.length > 0
    && (aggSnap.data?.upToDate === false || aggSnap.data?.cells === undefined);

  /**
   * Bundle headers carry their own pre-aggregated row totals; the server
   * aggregator excludes them from the table-wide totals (see
   * `computeAggregations`) so they aren't double-counted
   * with their member rows. Mirror that here.
   *
   * Formulas are computed per row rather than read from the persisted cache, for
   * the reason `computeAggregations` does the same: the cache is the trigger's to
   * write and can lag the operands on screen (SYD-2292). One evaluation per row
   * serves every aggregated column.
   */
  const formulaConfig = createArFormulaEngineConfig(schemaSnap.data as ArTableType | undefined);
  const pdfRowsForAggregation: ArAggregationRow[] | null = pdfExportMode && pdfRowIds
    ? pdfRowIds
        .map((id) => ({ rowId: id, data: rowsSnap.docs.get(id)?.data as ArRowData | undefined }))
        .filter((row): row is { rowId: ArRowId; data: ArRowData } =>
          !!row.data && row.data.bundleRole !== 'header')
        .map(({ rowId, data }) => {
          const computation = computeArRowFormulas(rowId, data, formulaConfig);
          return {
            cells: data.cells ?? {},
            formulaResults: computation.skipped ? undefined : computation.results,
          };
        })
    : null;

  /**
   * Draws nothing. It exists only so the PDF capture waits for a fresh total:
   * `PDF_LOADING_SELECTOR` in `callGeneratePdf` polls this label out of the DOM
   * before shooting, and a stale total on screen carries no other marker.
   */
  const computingMarker = <span aria-label="artable-aggregation-loading" />;

  /** The element showing a stale total goes quiet; the aggregation name never does. */
  const calculatingClass = isComputing ? 'calculating-value' : undefined;

  /**
   * Names the row, so a reader of the exported quote knows the last line is the
   * total and not a sixth line item. The first column carries it whatever that
   * column is: an aggregated first column holds the label and its own total
   * side by side, the label fixed and the value free to shrink (SYD-2901).
   */
  const totalsLabel = (
    <Text style={{ flexShrink: 0, marginInlineEnd: token.marginXXS }}>Total</Text>
  );

  /**
   * A data cell adds no padding of its own: the antd control inside it is what insets the
   * value, by `paddingSM - lineWidth` (`antd/lib/input/style/token.js`). A total rendered
   * through `ArCellRouter` carries that same control and so needs no cell padding, while a
   * total rendered as bare text carries none and must supply the inset itself. Without this
   * split the totals row sits a control's padding to the right of the column above it.
   */
  const controlValueInset = token.paddingSM - token.lineWidth;

  const cells: JSX.Element[] = [];

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    const fieldTypeForColumn = column.uiElementType === UIElementType.Field
      ? fieldTypes[column.typeId]
      : undefined;
    /**
     * Right-align totals whose aggregation produces a number — the aggregation
     * itself (`sum|avg|min|max`) is the load-bearing signal, not the field
     * type, so this works for composite numeric fields like `sellPriceUI`
     * without an explicit allowlist.
     */
    const columnAggregation = column.uiElementType === UIElementType.Field
      ? (column as ArColumnField).aggregation
      : undefined;
    if (cardMode && !columnAggregation) continue;
    const leadLabel = !cardMode && i === 0 ? totalsLabel : null;
    if (cardMode) {
      cells.push(
        <Text
          key={`${column.typeId}-label`}
          type="secondary"
          style={{ gridColumnStart: 1, gridColumnEnd: -1 }}
        >
          {resolveArColumnLabel(column, fieldTypes)}
        </Text>
      );
    }
    const isNumericAggregation =
      pdfExportMode &&
      columnAggregation !== undefined &&
      columnAggregation !== 'count';

    const cellStyle: CSSProperties = {
      position: 'relative',
      maxHeight: pdfExportMode ? undefined : token.controlHeight,
      minHeight: token.controlHeight,
      display: 'flex',
      alignItems: pdfExportMode ? 'flex-start' : 'center',
      justifyContent: isNumericAggregation ? 'flex-end' : undefined,
      borderRight: cardMode ? 'none' : `2px solid ${token.colorBorderSecondary}60`,
      gridColumnStart: cardMode ? 1 : undefined,
      gridColumnEnd: cardMode ? -1 : undefined,
      borderRadius: 0,
      backgroundColor: token.colorBgElevated,
      boxSizing: 'border-box',
      minWidth: 0,
      overflow: pdfExportMode ? 'visible' : 'hidden',
      overflowWrap: pdfExportMode ? 'anywhere' : undefined,
      wordBreak: pdfExportMode ? 'break-word' : undefined,
      whiteSpace: pdfExportMode ? 'normal' : undefined,
      width: '100%',
      flex: 1,
      paddingInlineStart: 0,
      paddingInlineEnd: controlValueInset,
    };

    if (!pdfExportMode && stickyFirstColumn && i < frozenLeftCount) {
      const left = frozenLeftOffsetsByIndex[i];
      if (left !== undefined) {
        cellStyle.position = 'sticky';
        cellStyle.left = left;
        cellStyle.zIndex = 20 + (frozenLeftCount - i);
      }
    }

    if (column.uiElementType !== UIElementType.Field) {
      cells.push(
        <div
          key={column.typeId}
          aria-label={`artable-aggregation-cell-${column.typeId}`}
          role="cell"
          style={cellStyle}
        >
          {leadLabel}
        </div>
      );
      continue;
    }

    const fieldColumn = column as ArColumnField;
    const aggregation = fieldColumn.aggregation;

    if (!aggregation) {
      cells.push(
        <div
          key={column.typeId}
          aria-label={`artable-aggregation-cell-${column.typeId}`}
          role="cell"
          style={cellStyle}
        >
          {leadLabel}
        </div>
      );
      continue;
    }

    const rawValue: unknown = pdfRowsForAggregation
      ? computeColumnAggregation(
          {
            typeId: column.typeId,
            aggregation,
            aggregationFieldPath: fieldColumn.aggregationFieldPath,
          },
          pdfRowsForAggregation,
        )
      : aggSnap.data?.cells?.[column.typeId]?.value ?? null;

    if (rawValue == null) {
      cells.push(
        <div
          key={column.typeId}
          aria-label={`artable-aggregation-cell-${column.typeId}`}
          role="cell"
          style={{ ...cellStyle, paddingInlineStart: controlValueInset }}
        >
          {leadLabel}
          {isComputing && (
            <span aria-label="artable-aggregation-loading" className="calculating-value">
              <Text type="secondary">—</Text>
            </span>
          )}
        </div>
      );
      continue;
    }

    const isCompound = fieldTypeForColumn?.fieldDataTypeId === 'composite' || fieldTypeForColumn?.fieldDataTypeId === 'sellPriceUI';

    if (isCompound && typeof rawValue === 'number') {
      const subFieldId = fieldColumn.aggregationFieldPath?.split('.')[1];
      const subFieldType = subFieldId ? fieldTypes[subFieldId] : fieldTypeForColumn;
      const { prefix, formatted, suffix } = formatFieldValueParts(rawValue, subFieldType);
      const formattedText = formattedCellValueToString(formatted);

      cells.push(
        <div
          key={column.typeId}
          aria-label={`artable-aggregation-cell-${column.typeId}`}
          role="cell"
          style={{ ...cellStyle, paddingInlineStart: controlValueInset }}
        >
          {leadLabel}
          {prefix && (
            <Text className={calculatingClass} style={{ marginRight: token.marginXXS }}>{prefix}</Text>
          )}
          <Text className={calculatingClass}>{formattedText}</Text>
          {suffix && (
            <Text className={calculatingClass} style={{ marginLeft: token.marginXXS }}>{suffix}</Text>
          )}
          <ArAggregationTag aggregation={aggregation} />
          {isComputing && computingMarker}
        </div>
      );
      continue;
    }

    const cellValue: ArCellValue = rawValue as ArCellValue;

    cells.push(
      <div
        key={column.typeId}
        aria-label={`artable-aggregation-cell-${column.typeId}`}
        role="cell"
        style={cellStyle}
      >
        {leadLabel}
        <span
          className={calculatingClass}
          style={{ display: 'flex', minWidth: 0, width: '100%' }}
        >
          <ArCellRouter
            column={column}
            value={cellValue}
            disabled
            cellId={`artable-aggregation-cell-inner-${column.typeId}`}
            isCalculated
            displayOnly
          />
        </span>
        <ArAggregationTag aggregation={aggregation} />
        {isComputing && computingMarker}
      </div>
    );
  }

  const totalsTopBorder = `${token.lineWidthBold}px solid ${token.colorText}`;

  const emptyCheckboxCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: totalsTopBorder,
    backgroundColor: token.colorBgElevated,
    boxSizing: 'border-box',
    position: !pdfExportMode && stickyFirstColumn ? 'sticky' : undefined,
    left: !pdfExportMode && stickyFirstColumn ? 0 : undefined,
    zIndex: !pdfExportMode && stickyFirstColumn ? 20 : undefined,
  };

  const emptyActionsCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: totalsTopBorder,
    backgroundColor: token.colorBgElevated,
    boxSizing: 'border-box',
    gridColumnEnd: -1,
    position: pdfExportMode ? 'static' : 'sticky',
    ...(pdfExportMode ? null : { right: 0, zIndex: 20 }),
  };

  if (cardMode) {
    return (
      <div
        aria-label="artable-aggregations"
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          gridColumn: '1 / -1',
          backgroundColor: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: token.paddingXS,
          rowGap: token.paddingXXS,
          overflow: 'hidden',
        }}
      >
        <Text strong style={{ gridColumn: '1 / -1' }}>Total</Text>
        {cells}
      </div>
    );
  }

  return (
    <div
      aria-label="artable-aggregations"
      role="row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumn: '1 / -1',
        backgroundColor: token.colorBgElevated,
        borderTop: totalsTopBorder,
      }}
    >
      {!pdfExportMode && (
        <div
          aria-label="artable-aggregation-checkbox"
          role="cell"
          style={emptyCheckboxCellStyle}
        />
      )}
      {cells}
      {!pdfExportMode && (
        <div
          aria-label="artable-aggregation-actions"
          role="cell"
          style={emptyActionsCellStyle}
        />
      )}
    </div>
  );
};


export default memo(ArTableAggregations);